import { detectRendererKind } from '../renderers/registry.js';

function iconForNode(node) {
  if (node.type === 'dir') return '📁';
  const kind = detectRendererKind(node.name || node.path || '');
  const icons = {
    code: '📄',
    markdown: '📝',
    html: '🌐',
    csv: '📊',
    xlsx: '📊',
    pdf: '📕',
    image: '🖼️',
    binary: '📦',
  };
  return icons[kind] || '📄';
}

function joinPath(parent, name) {
  const base = (parent || '').replace(/\/+$/, '');
  const segment = (name || '').replace(/^\/+/, '');
  return base ? `${base}/${segment}` : segment;
}

function parentDir(path) {
  if (!path || !path.includes('/')) return '';
  return path.slice(0, path.lastIndexOf('/'));
}

export class FileTree {
  constructor({ container, onSelect, onError, readonly = false }) {
    this.container = container;
    this.onSelect = onSelect;
    this.onError = onError;
    this.readonly = readonly;
    this.expanded = new Set(['']);
    this.selectedPath = null;
    this.nodesByDir = new Map();
    this.loadingDirs = new Set();
    this.listTreeFn = null;
    this.dirtyPaths = new Set();
    this.actionHandlers = {};
    this._renderShell();
  }

  setListTreeFn(fn) {
    this.listTreeFn = fn;
  }

  setActionHandlers(handlers = {}) {
    this.actionHandlers = handlers;
  }

  getContextDir() {
    if (!this.selectedPath) return '';
    const nodeType = this._findNodeType(this.selectedPath);
    return nodeType === 'dir' ? this.selectedPath : parentDir(this.selectedPath);
  }

  _findNodeType(path) {
    for (const [dirPath, nodes] of this.nodesByDir.entries()) {
      const match = nodes.find((node) => node.path === path);
      if (match) return match.type;
    }
    return path.includes('.') ? 'file' : 'dir';
  }

  markDirty(path, dirty = true) {
    if (dirty) this.dirtyPaths.add(path);
    else this.dirtyPaths.delete(path);
    this._renderTree();
  }

  async refresh(path = '') {
    if (!this.listTreeFn) return;
    const nodes = await this.listTreeFn(path);
    this.nodesByDir.set(path, nodes);
    this._renderTree();
  }

  async expandDir(path) {
    if (this.expanded.has(path)) {
      this.expanded.delete(path);
      this._renderTree();
      return;
    }
    this.expanded.add(path);
    if (!this.nodesByDir.has(path) && this.listTreeFn) {
      this.loadingDirs.add(path);
      this._renderTree();
      try {
        const nodes = await this.listTreeFn(path);
        this.nodesByDir.set(path, nodes);
      } catch (err) {
        this.onError?.(err);
      } finally {
        this.loadingDirs.delete(path);
      }
    }
    this._renderTree();
  }

  selectPath(path) {
    this.selectedPath = path;
    this._renderTree();
    this.onSelect?.(path);
  }

  _renderShell() {
    this.container.innerHTML = `
      <div class="repo-tree">
        <div class="repo-tree-header">
          <span>Files</span>
          <div class="repo-tree-actions">
            ${this.readonly ? '' : `
              <button type="button" class="repo-tree-action" data-repo-tree-action="file" title="New file">+ File</button>
              <button type="button" class="repo-tree-action" data-repo-tree-action="folder" title="New folder">+ Folder</button>
              <button type="button" class="repo-tree-action" data-repo-tree-action="upload" title="Upload files">Upload</button>
              <input type="file" class="repo-tree-upload-input" data-repo-tree-upload multiple hidden />
            `}
            <button type="button" class="repo-tree-refresh" title="Refresh">↻</button>
          </div>
        </div>
        <div class="repo-tree-body" data-repo-tree-body></div>
      </div>
    `;
    this.bodyEl = this.container.querySelector('[data-repo-tree-body]');
    this.container.querySelector('.repo-tree-refresh')?.addEventListener('click', () => {
      this.nodesByDir.clear();
      this.refresh('').catch((err) => this.onError?.(err));
    });

    if (!this.readonly) {
      this.uploadInput = this.container.querySelector('[data-repo-tree-upload]');
      this.container.querySelectorAll('[data-repo-tree-action]').forEach((button) => {
        button.addEventListener('click', () => {
          const action = button.dataset.repoTreeAction;
          if (action === 'upload') {
            this.uploadInput?.click();
            return;
          }
          this._handleCreateAction(action).catch((err) => this.onError?.(err));
        });
      });
      this.uploadInput?.addEventListener('change', () => {
        const files = Array.from(this.uploadInput.files || []);
        this.uploadInput.value = '';
        if (!files.length) return;
        const parent = this.getContextDir();
        this.actionHandlers.onUpload?.(parent, files)
          ?.then(() => this.refresh(''))
          ?.catch((err) => this.onError?.(err));
      });
    }
  }

  async _handleCreateAction(action) {
    const parent = this.getContextDir();
    const label = action === 'folder' ? 'folder name' : 'file path';
    const name = window.prompt(`Enter ${label}${parent ? ` inside ${parent}` : ''}:`, '');
    if (!name) return;
    const path = joinPath(parent, name.trim());
    if (!path) return;
    if (action === 'folder') {
      await this.actionHandlers.onMkdir?.(path);
    } else {
      await this.actionHandlers.onNewFile?.(path);
      this.selectPath(path);
    }
    await this.refresh('');
    if (parent) {
      this.expanded.add(parent);
      await this.refresh(parent);
    }
  }

  _renderTree() {
    if (!this.bodyEl) return;
    const html = this._renderDir('', 0);
    this.bodyEl.innerHTML = html || '<div class="repo-tree-empty">Empty repository</div>';
    this.bodyEl.querySelectorAll('[data-tree-path]').forEach((el) => {
      el.addEventListener('click', (event) => {
        event.stopPropagation();
        const path = el.getAttribute('data-tree-path');
        const type = el.getAttribute('data-tree-type');
        if (type === 'dir') this.expandDir(path).catch((err) => this.onError?.(err));
        else this.selectPath(path);
      });
    });
  }

  _renderDir(path, depth) {
    const nodes = this.nodesByDir.get(path);
    if (path && !this.expanded.has(path)) return '';
    if (this.loadingDirs.has(path)) {
      return `<div class="repo-tree-loading" style="padding-left:${depth * 14}px">Loading…</div>`;
    }
    if (!nodes) return '';
    return nodes.map((node) => {
      const isDir = node.type === 'dir';
      const expanded = this.expanded.has(node.path);
      const selected = this.selectedPath === node.path ? ' is-selected' : '';
      const dirty = this.dirtyPaths.has(node.path) ? ' is-dirty' : '';
      const caret = isDir ? (expanded ? '▾' : '▸') : ' ';
      const row = `
        <button type="button" class="repo-tree-item${selected}${dirty}" data-tree-path="${node.path}" data-tree-type="${node.type}" style="padding-left:${depth * 14 + 8}px">
          <span class="repo-tree-caret">${caret}</span>
          <span class="repo-tree-icon">${iconForNode(node)}</span>
          <span class="repo-tree-label">${node.name}</span>
        </button>
      `;
      const children = isDir && expanded ? this._renderDir(node.path, depth + 1) : '';
      return row + children;
    }).join('');
  }
}
